// Local-storage + deep-link URL helpers for Tracker.jsx.
// Extracted so Tracker stays focused on rendering + state.

export const STORAGE_KEYS = {
  sports: "tracker.selectedSports.v1",
  tz: "tracker.timezone.v1",
  streamerFilter: "tracker.streamerFilter.v1",
  special: "tracker.special.v1",
  month: "tracker.monthFilter.v1",
  week: "tracker.weekFilter.v1",
  quick: "tracker.quickFilter.v1",
  rangeStart: "tracker.rangeStart.v1",
};

// Range filter — user picks any date → show that day + the following 6 days.
export const RANGE_DAYS = 7;

/** YYYY-MM-DD validator for the range-start param. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const QUICK_OPTIONS = [
  { id: "today", label: "Tonight" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "weekend", label: "This weekend" },
];

const QUICK_IDS = new Set(QUICK_OPTIONS.map((o) => o.id));

export function readSavedSports(allSports) {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.sports);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((s) => allSports.includes(s));
  } catch {}
  return [];
}
export function readSavedTz() {
  try { return localStorage.getItem(STORAGE_KEYS.tz) || "auto"; } catch { return "auto"; }
}
export function readSavedStreamerFilter() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.streamerFilter);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
export function readSavedSpecial() {
  try { return localStorage.getItem(STORAGE_KEYS.special) === "true"; } catch { return false; }
}
export function readSavedMonth() {
  try { return localStorage.getItem(STORAGE_KEYS.month) || ""; } catch { return ""; }
}
export function readSavedWeek() {
  try { return localStorage.getItem(STORAGE_KEYS.week) || ""; } catch { return ""; }
}
export function readSavedQuick() {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.quick) || "";
    return QUICK_IDS.has(v) ? v : "";
  } catch { return ""; }
}
export function readSavedRangeStart() {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.rangeStart) || "";
    return ISO_DATE_RE.test(v) ? v : "";
  } catch { return ""; }
}

/**
 * Read deep-link query params. URL values take priority over localStorage on
 * the first render so that someone opening a shared link sees that exact view,
 * not whatever was last saved on their device.
 */
export function readUrlParams(allSports) {
  if (typeof window === "undefined") return {};
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    if (p.has("sports")) {
      out.sports = (p.get("sports") || "")
        .split(",").map((s) => s.trim()).filter((s) => allSports.includes(s));
    }
    if (p.get("fifa") === "1") out.special = true;
    const q = p.get("quick");
    if (q && QUICK_IDS.has(q)) out.quick = q;
    const m = p.get("month");
    if (m && /^\d{4}-\d{2}$/.test(m)) out.month = m;
    const w = p.get("week");
    if (w && /^\d{4}-\d{2}-\d{2}$/.test(w)) out.week = w;
    const r = p.get("from");
    if (r && ISO_DATE_RE.test(r)) out.rangeStart = r;
    if (p.has("streamers")) {
      out.streamers = (p.get("streamers") || "")
        .split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (p.has("tz")) out.tz = p.get("tz");
    out.autofetch = p.has("sports") || p.get("fifa") === "1";
    return out;
  } catch { return {}; }
}

/** Build a shareable deep-link URL that captures the user's current view. */
export function buildShareUrl({ selected, specialOn, quickFilter, monthFilter, weekFilter, rangeStart, streamerFilter, timezone }) {
  const p = new URLSearchParams();
  if (selected?.length) p.set("sports", selected.join(","));
  if (specialOn) p.set("fifa", "1");
  if (quickFilter) p.set("quick", quickFilter);
  if (monthFilter) p.set("month", monthFilter);
  if (weekFilter) p.set("week", weekFilter);
  if (rangeStart) p.set("from", rangeStart);
  if (streamerFilter?.length) p.set("streamers", streamerFilter.join(","));
  if (timezone && timezone !== "auto") p.set("tz", timezone);
  const qs = p.toString();
  const base = `${window.location.origin}${window.location.pathname}`;
  return qs ? `${base}?${qs}` : base;
}

/**
 * Copy text to the clipboard with a `document.execCommand('copy')` fallback
 * for non-secure contexts. Returns true if the copy succeeded.
 */
export async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {}
  return false;
}
